import axios from 'axios';

/**
 * Returns the authorization header with the JWT token
 * @returns Header object with Authorization token or empty object if not authenticated
 */
const getToken = (): string | null =>
  localStorage.getItem('token') || sessionStorage.getItem('token');

export const getAuthHeader = () => {
  const token = getToken();
  if (token) {
    return { 'Authorization': `Bearer ${token}` };
  }
  return {};
};

/**
 * Handle login request
 * @param username User's email address
 * @param password User's password
 * @returns Response with token and user data
 */
export const login = async (username: string, password: string) => {
  try {
    const response = await axios.post('/api/auth/login', { username, password });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Handle logout - clears token from localStorage and axios headers
 */
export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
  delete axios.defaults.headers.common['Authorization'];
};

/**
 * Check if user is authenticated
 * @returns Boolean indicating if user has valid token
 */
export const isAuthenticated = (): boolean => !!getToken();

/**
 * Get current user from localStorage
 * @returns User object or null if not authenticated
 */
export const getCurrentUser = () => {
  const userData = localStorage.getItem('user') || sessionStorage.getItem('user');
  if (userData) {
    try {
      return JSON.parse(userData);
    } catch (error) {
      return null;
    }
  }
  return null;
};

// Global axios response interceptor for 401 Unauthorized
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      // Redirect to login page if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
); 